import { periodLabel } from "./groupFormat";
import { groupPhase } from "./groupList";

/**
 * The Portal tab's group list — how a group is named, sorted and described so
 * it can be told apart at a glance. Group names here run long and alike
 * ("Grupă pentru discipline umaniste & STEM (S5G6)"); what the team looks for
 * is the code, the dates and whether the cohort is running.
 */

/** "S5G6" out of "… (S5G6)", or "" when the name carries no code. */
export function groupCode(name) {
  const m = String(name || "").match(/\(\s*([A-Z]\d+\s*G\d+)\s*\)/i);
  return m ? m[1].replace(/\s+/g, "").toUpperCase() : "";
}

const fold = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** The name without what the code, the eyebrow and the chips already say. */
export function shortName(name) {
  const raw = String(name || "");
  let s = raw
    .replace(/\(\s*[A-Z]\d+\s*G\d+\s*\)/i, " ")
    .replace(/^\s*grup[aă]\s+pentru\s+/i, "")
    .replace(/\s*[-–]\s*anulat[aă]?\s*$/i, "")
    .replace(/\s*&\s*/g, " și ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return raw.trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Cancelled cohorts are marked in their name, or switched off. */
export function isCancelled(group) {
  return /anulat/.test(fold(group?.name)) || group?.status === "inactive";
}

export const BUCKETS = [
  { key: "in_curs", label: "În desfășurare", openByDefault: true },
  { key: "urmeaza", label: "Urmează", openByDefault: true },
  { key: "incheiata", label: "Încheiate", openByDefault: false },
  { key: "anulate", label: "Anulate sau inactive", openByDefault: false },
];

export function bucketOf(group, now = new Date()) {
  return isCancelled(group) ? "anulate" : groupPhase(group, now);
}

/** Groups by bucket, in the order the tab shows them. Running and coming ones
 *  soonest first; finished ones latest first. */
export function bucketed(groups, now = new Date()) {
  const out = Object.fromEntries(BUCKETS.map((b) => [b.key, []]));
  (groups || []).forEach((g) => out[bucketOf(g, now)].push(g));
  const byStart = (a, b) => String(a.start_date || "").localeCompare(String(b.start_date || ""));
  out.in_curs.sort(byStart);
  out.urmeaza.sort(byStart);
  out.incheiata.sort((a, b) => byStart(b, a));
  out.anulate.sort((a, b) => byStart(b, a));
  return out;
}

export function period(group) {
  return group?.start_date ? periodLabel(group.start_date, group.end_date) : "fără dată";
}

const people = (n, one, many) => (n === 1 ? one : `${n} ${many}`);

/** "14 cu contract semnat din 18" — who would get in if it opened now. */
export function readinessLine(g) {
  const signed = g?.signed || 0;
  const places = g?.places || 0;
  if (!places) return "niciun cursant încă";
  const base = `${signed} cu contract semnat`;
  return places > signed ? `${base} din ${places}` : base;
}

/** What the confirmation says before a group's portal is opened. Since
 *  08.10.2026 it opens for the trainer only; participants are invited by the
 *  team, from this page, once the group is ready. The trainer is named by
 *  what they do, not by a gendered noun the name cannot settle. */
export function openConfirmText(g) {
  const teacher = g?.trainer_name ? `${g.trainer_name}, care predă grupa` : "cine predă grupa";
  return `Portalul se deschide doar pentru ${teacher}: primește un email ca să pregătească temele și `
    + "materialele. Cursanții nu primesc nimic și nu pot intra până nu le trimiți invitațiile, de aici.";
}

/** Where an open group stands with its participants: a chip and a line. */
export function participantsState(g) {
  if (g?.portal_participants_opened_at) {
    return { key: "opened", tone: "good", label: "cursanți invitați" };
  }
  if (g?.portal_trainer_ready_at) return { key: "ready", tone: "info", label: "formatorul e gata" };
  return { key: "trainer_only", tone: "warn", label: "doar formatorul" };
}

/** The confirmation before "Trimite invitațiile cursanților", from the preview. */
export function inviteParticipantsText(p) {
  const lines = [];
  lines.push(p.invite_now
    ? `Pleacă acum ${people(p.invite_now, "o invitație", "invitații")}, către cursanții cu contractul semnat.`
    : "Niciun cursant cu contract semnat nu așteaptă invitația acum.");
  if (p.not_signed) {
    lines.push(`${people(p.not_signed, "Un cursant nu a", "cursanți nu au")} semnat încă: `
      + `${p.not_signed === 1 ? "primește" : "primesc"} invitația automat când semnează.`);
  }
  const ready = [p.prepared?.assignments && people(p.prepared.assignments, "o temă", "teme"),
    p.prepared?.materials && people(p.prepared.materials, "un material", "materiale")].filter(Boolean);
  lines.push(ready.length ? `În portal sunt deja ${ready.join(" și ")}; invitația le menționează.`
    : "În portal nu e încă nicio temă sau material publicat.");
  return lines;
}
/** In the invitations' dialog, once the trainer has said the group is ready (15.10.2026). */
export function readyNoteLine(p) {
  if (!p?.trainer_ready_at) return "";
  const day = new Intl.DateTimeFormat("ro-RO", { timeZone: "Europe/Bucharest", day: "numeric", month: "long" })
    .format(new Date(p.trainer_ready_at));
  return `Formatorul a anunțat pe ${day}` + (p.trainer_ready_note ? `: „${p.trainer_ready_note}”` : ".");
}

/** And before it is closed: who loses access, and what does not go away. */
export function stopConfirmText(g) {
  const n = g?.signed || 0;
  const who = n ? `${people(n, "Un cursant", "cursanți")} și cine predă grupa pierd` : "Cine predă grupa pierde";
  return `${who} accesul imediat. Ce s-a încărcat rămâne la noi și reapare dacă redeschizi portalul.`;
}
